import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React from "react";
import CloseIcon from '@mui/icons-material/Close';
import styles from "./LogisticsFeeDialog.module.scss";
import { format } from "date-fns";
import DateSelect from "../../core/DateSelect";
import InputGroup from "../../core/InputGroup";
import { LogisticsFeeResponseData } from "../../../apis/models/LogisticsFeeResponse";
import { BrandlistResponseData } from "../../../apis/models/BrandlistResponse";
import { InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import { dateToString, isInvalidDate, nowAsString } from "../../../libs/helpers";

type InputField = {
  dateAdded: string,
  productNumber: string,
  colors: string,
  inspectionUnitPrice: string,
  inspectionQuantity: string,
  shippingUnitPrice: string,
  shippingQuantity: string,
  memo: string,
}

type ErrorInfo = {
  dateAdded?: string;
  inspectionQuantity?: string;
  inspectionUnitPrice?: string;
  shippingQuantity?: string;
  shippingUnitPrice?: string;
}

type RowType = InputField & {
  error: ErrorInfo
}

const initialItem: RowType = {
  dateAdded: nowAsString(),
  productNumber: "",
  colors: "",
  inspectionQuantity: "",
  inspectionUnitPrice: "",
  shippingQuantity: "",
  shippingUnitPrice: "",
  memo: "",
  error: {},
}

const createInitialItem = () => {
  return {
    dateAdded: nowAsString(),
    productNumber: "",
    colors: "",
    inspectionQuantity: "",
    inspectionUnitPrice: "",
    shippingQuantity: "",
    shippingUnitPrice: "",
    memo: "",
    error: {},
  }
}

const LogisticsFeeDialog: React.FC<LogisticsFeeDialogProps> = (props: LogisticsFeeDialogProps) => {
    const {targetBrand, targetInvoice, open, onClose, onSave} = props;
    const [dialogData, setDialogData] = React.useState<RowType[]>([createInitialItem()]);

    const handleInputChange = (item: RowType, index: number) => {
      if (Object.keys(item.error).length > 1) {
        item.error = validateRow(item)
      }
      const copyData = [...dialogData];
      copyData.splice(index, 1, item);
      setDialogData(copyData);
    }

    const deleteItem = (item: number) => {
      if (dialogData.length <= 1) {
        return
      }
      const copyData = [...dialogData];
      copyData.splice(item, 1);
      setDialogData(copyData);
    }
    
    const handleSave = () => {
      if (isDisabledSave()) {
        return
      }
      const responseData = dialogData.map(item => {
        return {
          dateAdded: item.dateAdded,
          productNumber: item.productNumber,
          colors: item.colors,
          inspectionUnitPrice: parseInt(item.inspectionUnitPrice),
          inspectionQuantity: parseInt(item.inspectionQuantity),
          shippingUnitPrice: parseInt(item.shippingUnitPrice),
          shippingQuantity: parseInt(item.shippingQuantity),
          memo: item.memo,
          invoiceMasterId: targetInvoice?.invoiceMasterId ?? "",
          logisticFeeId: "",
          created: "",
          modified: "",
        }
      })
      onSave(responseData)
    }

    const isDisabledSave = () => {
      if (dialogData.length <= 0) {
        return true
      }
      return dialogData.some((item, index) => {
        const err = validateRow(item)
        item.error = err
        const newItems = [...dialogData]
        newItems.splice(index, 1, item)
        setDialogData(newItems)
        return err.dateAdded || err.inspectionQuantity || err.inspectionUnitPrice || err.shippingQuantity || err.shippingUnitPrice
      })
    }

    const validateRow = (item: RowType) => {
      const error: ErrorInfo = {
        dateAdded: validateDateAdded(item.dateAdded),
        inspectionQuantity: validateNumber(item.inspectionQuantity),
        inspectionUnitPrice: validateNumber(item.inspectionUnitPrice),
        shippingQuantity: validateNumber(item.shippingQuantity),
        shippingUnitPrice: validateNumber(item.shippingUnitPrice)
      }
      return error
    }

    const validateDateAdded = (value: string) => {
      if (value.length === 0) {
        return "入力必須です"
      } else if (isInvalidDate(value)) {
        return "年月日で入力してください"
      } else if (!targetInvoice) {
        return "請求期間の範囲で入力してください"
      } else if (targetInvoice) {
        const invoiceFrom = format(new Date(targetInvoice.from), "yyyy/MM/dd")
        const invoiceTo = format(new Date(targetInvoice.to), "yyyy/MM/dd")
        if (value < invoiceFrom || invoiceTo < value) {
          return "請求期間の範囲で入力してください"
        }
      }
      return undefined
    }
    
    const validateNumber = (value: string) => {
      if (value.length === 0) {
        return "入力必須です"
      } else if (isNaN(+value) || parseInt(value) < 1) {
        return "１以上を入力してください"
      }
      return undefined
    }

    const invoicePeriod = React.useMemo(() => {
      if (targetInvoice){
        return `${format(new Date(targetInvoice.from), "yyyy/MM/dd")} ～ ${format(new Date(targetInvoice.to), "yyyy/MM/dd")}`
      }
      return ''
    }, [targetInvoice])

    return (
        <Dialog open={open} onClose={() => {setDialogData([initialItem]); onClose()}} fullWidth={true} maxWidth="lg" PaperProps={{sx: {height: '90%'}}}>
            <DialogTitle sx={{fontWeight: 700, fontSize: "24px", paddingLeft: "40px"}}>{targetBrand?.brandName ?? ''} - 物流費追加</DialogTitle>
            <DialogContent sx={{padding: "0 40px 24px", display: "flex", flexDirection: "column"}}>
                <div className={styles.dialog__label}>
                  <span>請求期間：</span>
                  <span className={styles.dialog__label_content}>{invoicePeriod}</span>
                </div>
                <div className={styles.dialog__content}>
                    {
                        dialogData.map((item, index) => {
                          return (
                            <div className={styles.dialog__content_row} key={index}>
                                <DateSelect selectedDate={item.dateAdded === "" ? null : new Date(item.dateAdded)} 
                                    handleDateChange={(date) => handleInputChange({...item, dateAdded: dateToString(date)}, index)}
                                    label="日付" required
                                    errMess={item.error.dateAdded}
                                />
                                <InputGroup label="品番" type="text" onChange={(val) => handleInputChange({...item, productNumber: val}, index)} value={item.productNumber}/>
                                <InputGroup label="色" type="text" onChange={(val) => handleInputChange({...item, colors: val}, index)} value={item.colors}/>
                                <InputGroup label="検品単価" type="number" onChange={(val) => handleInputChange({...item, inspectionUnitPrice: val}, index)} 
                                    value={item.inspectionUnitPrice} required error={!!item.error.inspectionUnitPrice} errorMessage={item.error.inspectionUnitPrice} />
                                <InputGroup label="検品枚数" type="number" onChange={(val) => handleInputChange({...item, inspectionQuantity: val}, index)} 
                                    value={item.inspectionQuantity} required error={!!item.error.inspectionQuantity} errorMessage={item.error.inspectionQuantity} />
                                <InputGroup label="出荷単価" type="number" onChange={(val) => handleInputChange({...item, shippingUnitPrice: val}, index)} 
                                    value={item.shippingUnitPrice} required error={!!item.error.shippingUnitPrice} errorMessage={item.error.shippingUnitPrice} />
                                <InputGroup label="出荷枚数" type="number" onChange={(val) => handleInputChange({...item, shippingQuantity: val}, index)} 
                                    value={item.shippingQuantity} required error={!!item.error.shippingQuantity} errorMessage={item.error.shippingQuantity} />
                                <InputGroup label="備考" type="text" onChange={(val) => handleInputChange({...item, memo: val}, index)} value={item.memo}/>
                                <CloseIcon className={styles.dialog__content_row_icon} onClick={() => deleteItem(index)}/>
                            </div>
                          )
                        })
                    }
                </div>
                <div className={styles.dialog__add}>
                    <Button variant="outlined" color="primary" onClick={() => {setDialogData([...dialogData, initialItem])}}>＋行追加</Button>
                </div>
                <div className={styles.dialog__functions}>
                    <Button variant="outlined" color="primary" onClick={() => {setDialogData([initialItem]); onClose()}}>キャンセル</Button>
                    <Button variant="contained" color="primary" onClick={() => handleSave()}>登録</Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default LogisticsFeeDialog;

export type LogisticsFeeDialogProps = {
    targetBrand?: BrandlistResponseData,
    targetInvoice?: InvoiceMasterData,
    open: boolean,
    onClose: () => void,
    onSave: (val: LogisticsFeeResponseData[]) => void,
    from?: "brandlist" | "invdetails",
}